import React, {
  CSSProperties,
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useIntl } from "react-intl";
import {
  ChoiceGroup,
  ContextualMenu,
  DefaultButton,
  Dialog,
  DialogType,
  MessageBar,
  MessageBarType,
  Stack,
} from "@fluentui/react";
import { IChoiceGroupOption } from "@fluentui/react/lib/ChoiceGroup";
import { useId } from "@fluentui/react-hooks";
import { LoadingOverlay, css, useTheme } from "cayo.ui";
import styled from "styled-components";
import { Schedule as BaseSchedule } from "@cayo/shared/api/types/cayo-graph";
import { HttpMethods } from "@cayo/shared/api/types/schema";
import { IClientComponentParameters } from "@cayo/shared/components/App/appContext";
import { FormProps } from "@cayo/shared/components/Form/types";
import ajax from "@cayo/shared/libs/ajax";
import logger from "@cayo/shared/libs/logger";
import { commonMessages } from "@cayo/shared/messages/common";
import useService from "@cayo/shared/services/hooks/useService";
import { IPropertiesPanelSignals } from "@cayo/shared/services/propertiesPanelSignal.service";
import { ajaxUtils } from "@cayo/shared/utils/ajax";
import { getObjectKeys } from "@cayo/shared/utils/typeUtils";
import continuousForm from "./continuouslyForm";
import customForm from "./customForm";
import dailyForm from "./dailyForm";
import hourlyForm from "./hourlyForm";
import messages from "./messages";
import minutelyForm from "./minutelyForm";
import monthlyForm from "./monthlyForm";
import notSetForm from "./notSetForm";
import oneTimeForm from "./oneTimeForm";
import scheduleTypes from "./types";
import weeklyForm from "./weeklyForm";
import FormFooter from "../Form/FormFooter";
import useForm from "../Form/hooks/useForm";
import Icon from "../Graphics/Icon";

type ScheduleProps = {
  url: string;
  method: HttpMethods;
  object?: BaseSchedule;
  isCustomView?: boolean;
  parameters?: string;
  style?: CSSProperties;
  parentObjectName?: string;
  signals?: IPropertiesPanelSignals;
  readОnly?: boolean;
} & IClientComponentParameters;

const Schedule: FC<ScheduleProps> = ({
  url,
  method,
  onClose,
  isCustomView,
  style,
  parameters,
  parentObjectName,
  signals,
  readОnly,
  ...other
}) => {
  const intl = useIntl();
  const { cayoTheme } = useTheme();

  const [selectedSchedule, setSelectedSchedule] = useState<scheduleTypes>(
    (other?.object?.objectType as scheduleTypes) ||
      (isCustomView ? "cayo.graph.notSetSchedule" : "cayo.graph.oneTimeSchedule")
  );

  const [schedule, setSchedule] = useState<Record<string, any>>({});
  const [switching, setSwitching] = useState(false);

  const actionBuilder = useService("actionBuilder");
  const [pastDateIsSelected, setPastDateIsSelected] = useState(false);

  logger.debug(other);
  const options = useMemo(() => {
    let allSchedules = getObjectKeys(scheduleMap);
    if (parameters?.length) {
      const parts = parameters.split("|");
      allSchedules = allSchedules.filter((s) => parts.findIndex((p) => s.indexOf(p) > 0) >= 0);
      const notSetSchedule = "cayo.graph.notSetSchedule" as scheduleTypes;
      if (!allSchedules.includes(notSetSchedule)) {
        allSchedules.unshift(notSetSchedule);
      }
    }

    return (
      allSchedules
        //.filter((k) => k !== "cayo.graph.minutelySchedule" && k !== "cayo.graph.hourlySchedule")
        .map<IChoiceGroupOption>((k) => ({
          key: k,
          text: scheduleMap[k](intl).title,
          disabled: readОnly,
        }))
    );
  }, [parameters, readОnly]);

  const realObject = useMemo(() => {
    const rObject = selectedSchedule === other?.object?.objectType ? other?.object : undefined;

    return schedule[selectedSchedule] || rObject;
  }, [selectedSchedule, other?.object, schedule]);

  const saveResponse = (response: { [key: string]: any }) => {
    if (response) {
      setSchedule({ [selectedSchedule]: response.schedule });
    }
  };

  const scheduleForm = useMemo<FormProps>(() => {
    const formProps = scheduleMap[selectedSchedule] as any;

    const hasChanges = selectedSchedule !== other?.object?.objectType;

    return {
      ...formProps(intl, { ...realObject, readОnly }),
      submit: { url, method },
      "@odata.type": "#" + selectedSchedule,
      bodyProperty: "schedule",
      intl,
      actionBuilder,
      schedule: realObject,
      onClose,
      ...other,
      object: realObject,
      parentObjectName,
      isInsidePropPanel: true,
      hasChanges,
      sendAllFields: true,
      onSubmitCB: saveResponse,
    } as FormProps;
  }, [selectedSchedule, other?.object, realObject]);

  const { isSubmitting, isSaveDisabled, errors, renderer, onSubmit, message, model, clearErrors } =
    useForm(scheduleForm, realObject);

  useEffect(() => {
    if (selectedSchedule === "cayo.graph.oneTimeSchedule") {
      const currentDate = new Date();
      const inputDate = new Date(model.startDateTime);
      setPastDateIsSelected(inputDate < currentDate);
    } else {
      setPastDateIsSelected(false);
    }
  }, [selectedSchedule, model]);

  const onChange = useCallback(
    (ev?: React.FormEvent<HTMLElement | HTMLInputElement>, option?: IChoiceGroupOption) => {
      clearErrors();
      if (option?.key) {
        setSelectedSchedule(option.key as scheduleTypes);
        setSwitching(true);
        setTimeout(() => setSwitching(false), 100);
      }
    },
    [clearErrors, selectedSchedule]
  );

  const deleteSchedule = useCallback(async () => {
    try {
      await ajax.getClient(url, "PATCH", undefined, { schedule: null });
      handleClose();
    } catch (e) {
      await ajaxUtils.handleError(e);
    }
  }, []);

  const labelId: string = useId("dialogLabel");
  const subTextId: string = useId("subTextLabel");

  const modalProps = useMemo(
    () => ({
      titleAriaId: labelId,
      subtitleAriaId: subTextId,
      isBlocking: true,
      styles: dialogStyles,
      dragOptions,
    }),
    [labelId, subTextId]
  );

  const handleClose = () => {
    return onClose ? onClose(false) : signals && signals.send("close");
  };

  if (
    style?.display === "none" ||
    (!Object.keys(model)?.length && selectedSchedule !== "cayo.graph.notSetSchedule")
  ) {
    return <Fragment />;
  }

  const content = (
    <>
      {isSubmitting && <LoadingOverlay />}{" "}
      {message?.message && (
        <MessageBar
          styles={{ root: { display: "flex", marginBottom: 12 } }}
          messageBarType={MessageBarType[message?.type ?? "success"]}
          isMultiline={true}
          truncated={true}
        >
          {message?.message}
        </MessageBar>
      )}
      <Stack
        horizontal={true}
        verticalAlign={"stretch"}
        styles={{ root: { height: "100%", width: "100%" } }}
        tokens={cayoTheme.metrics.stackTokens}
      >
        <ChoiceGroup
          defaultSelectedKey={selectedSchedule}
          options={options}
          onChange={onChange}
          styles={{
            label: { whiteSpace: "nowrap", textOverflow: "ellipsis" },
            root: { minWidth: 130, borderRight: `1px solid ${cayoTheme.brandColors.divider}` },
          }}
        />

        <StyledForm
          key={selectedSchedule}
          onSubmit={onSubmit}
          noValidate={true}
          scheduleFormStyle={(scheduleForm as any)?.style}
        >
          <Stack
            horizontalAlign="stretch"
            styles={{ root: { width: "100%" } }}
            tokens={cayoTheme.metrics.stackTokens}
          >
            {switching ? null : renderer()}
          </Stack>
        </StyledForm>
      </Stack>
      <div className={css.relative}>
        <FormFooter
          isOKDisabled={isSaveDisabled || pastDateIsSelected}
          errors={errors}
          onClose={handleClose}
          onSubmit={onSubmit}
          saveError={""}
          footer={{
            okText: intl.formatMessage(commonMessages.save),
            cancelText: isCustomView ? undefined : intl.formatMessage(commonMessages.cancel),
          }}
        />
        {!isCustomView && (
          <DefaultButton
            styles={{ root: { position: "absolute", left: 0, bottom: 0 } }}
            onClick={deleteSchedule}
          >
            <Stack horizontal={true}>
              <Icon iconName="Calendar+Cancel" />
              {intl.formatMessage(commonMessages.remove)}
            </Stack>
          </DefaultButton>
        )}
      </div>
    </>
  );

  if (isCustomView) {
    return (
      <Stack
        styles={{
          root: { ...(style as {}), padding: "10px 20px 24px 20px" },
        }}
      >
        {content}
      </Stack>
    );
  }

  return (
    <Dialog
      dialogContentProps={{
        type: DialogType.normal,
        title: intl.formatMessage(messages.scheduleDialogTitle),
        showCloseButton: true,

        styles: {
          subText: {
            marginTop: 8,
          },
          innerContent: { height: "100%", width: "100%" },
        },
      }}
      styles={{
        main: {
          minWidth: "600px !important",
        },
      }}
      hidden={false}
      modalProps={modalProps}
      onDismiss={handleClose}
    >
      {content}
    </Dialog>
  );
};

const dialogStyles = { main: { maxWidth: "460px !important" } };

const dragOptions = {
  moveMenuItemText: "Move",
  closeMenuItemText: "Close",
  menu: ContextualMenu,
};

type getFormFuncType = typeof oneTimeForm;

const scheduleMap: Record<scheduleTypes, getFormFuncType> = {
  "cayo.graph.notSetSchedule": notSetForm,
  "cayo.graph.oneTimeSchedule": oneTimeForm,
  "cayo.graph.dailySchedule": dailyForm,
  "cayo.graph.weeklySchedule": weeklyForm,
  "cayo.graph.monthlySchedule": monthlyForm,

  "cayo.graph.hourlySchedule": hourlyForm,
  "cayo.graph.minutelySchedule": minutelyForm,

  "cayo.graph.continuousSchedule": continuousForm,
  "cayo.graph.customSchedule": customForm,
};

export const StyledForm = styled.form<{ scheduleFormStyle?: CSSProperties }>`
  display: flex;
  width: 100%;
  flex-flow: column;
  min-height: 280px;
  align-items: flex-start;
  padding-left: 8px;
  ${(props) => ({ ...props.scheduleFormStyle })};
`;

export default Schedule;
